import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import PropTypes from "prop-types";

const COLORS = [
  "#8884d8",
  "#83a6ed",
  "#8dd1e1",
  "#82ca9d",
  "#a4de6c",
  "#d0ed57",
  "#ffc658",
  "#ff8042",
  "#ff6361",
  "#bc5090",
];

const Category90DayAverage = ({ data, formatCurrency, isLoading }) => {
  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-80 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <p>No historical average data available</p>
      </div>
    );
  }

  const periodDays = data[0]?.period_days || 180;
  const periodMonths = Math.round(periodDays / 30);

  // Custom tooltip to show detailed information
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            <p className="text-blue-600">
              <span className="font-medium">Recommended Budget:</span>{" "}
              {formatCurrency(data.recommended_monthly_budget || data.statistical_monthly_projection)}
            </p>
            <p className="text-green-600">
              <span className="font-medium">Daily Average:</span>{" "}
              {formatCurrency(data.avg_daily_amount)}
            </p>
            <p className="text-purple-600">
              <span className="font-medium">{periodDays}-Day Total:</span>{" "}
              {formatCurrency(data.total_period_amount)}
            </p>
            <p className="text-orange-600">
              <span className="font-medium">Yearly Projection:</span>{" "}
              {formatCurrency(data.statistical_yearly_projection || data.simple_yearly_projection)}
            </p>
            <p className="text-red-600">
              <span className="font-medium">Variability:</span>{" "}
              {data.transaction_variability_percent?.toFixed(1)}% {data.has_transaction_outliers ? "(has outliers)" : ""}
            </p>
            <p className="text-gray-600">
              <span className="font-medium">Days with Spending:</span>{" "}
              {data.days_with_spending} / {periodDays} ({data.spending_frequency_percent}%)
            </p>
            <p className="text-gray-600">
              <span className="font-medium">Avg per Active Day:</span>{" "}
              {formatCurrency(data.avg_per_spending_day)}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full">
      <h2 className="text-lg font-semibold text-gray-700 mb-4">
        {periodMonths}-Month Historical Category Averages
      </h2>
      
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600">
          Based on {periodDays} days ({periodMonths} months) of spending data, showing projected monthly averages 
          for your top spending categories. Longer periods provide more reliable seasonal patterns.
          <br />
          <span className="text-xs text-gray-500 mt-1">
            Hover over bars for detailed breakdown including yearly projections.
          </span>
        </p>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            tickFormatter={(value) => formatCurrency(value)}
          />
          <YAxis 
            type="category" 
            dataKey="name" 
            width={90}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={CustomTooltip} />
          <Bar dataKey="recommended_monthly_budget" fill="#8884d8" radius={[0, 4, 4, 0]}>
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.has_transaction_outliers ? "#ef4444" : COLORS[index % COLORS.length]}
                opacity={entry.transaction_variability_percent > 100 ? 0.8 : 1}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Summary statistics */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600 mb-1">Total Recommended Budget</div>
          <div className="text-xl font-bold text-blue-600">
            {formatCurrency(data.reduce((sum, item) => sum + (item.recommended_monthly_budget || item.statistical_monthly_projection || 0), 0))}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Smart budget recommendations
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600 mb-1">Total Yearly Projected</div>
          <div className="text-xl font-bold text-green-600">
            {formatCurrency(data.reduce((sum, item) => sum + (item.statistical_yearly_projection || item.simple_yearly_projection || 0), 0))}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Statistical yearly projection
          </div>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600 mb-1">Top Category Budget</div>
          <div className="text-xl font-bold text-purple-600">
            {data.length > 0 ? formatCurrency(data[0].recommended_monthly_budget || data[0].statistical_monthly_projection || 0) : formatCurrency(0)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {data.length > 0 ? data[0].name : 'No data'}
            {data.length > 0 && data[0].has_transaction_outliers ? ' ⚠️' : ''}
          </div>
        </div>

        <div className="bg-orange-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600 mb-1">Period Analyzed</div>
          <div className="text-xl font-bold text-orange-600">
            {periodDays} days
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {periodMonths} months of data
          </div>
        </div>
      </div>
    </div>
  );
};

Category90DayAverage.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      period_days: PropTypes.number,
      avg_daily_amount: PropTypes.number.isRequired,
      // New enhanced fields
      recommended_monthly_budget: PropTypes.number,
      statistical_monthly_projection: PropTypes.number,
      simple_monthly_projection: PropTypes.number,
      statistical_yearly_projection: PropTypes.number,
      simple_yearly_projection: PropTypes.number,
      // Legacy fields (for backward compatibility)
      avg_monthly_amount: PropTypes.number,
      projected_yearly_amount: PropTypes.number,
      // Required fields
      days_with_spending: PropTypes.number.isRequired,
      total_period_amount: PropTypes.number.isRequired,
      avg_per_spending_day: PropTypes.number.isRequired,
      transaction_count: PropTypes.number.isRequired,
      spending_frequency_percent: PropTypes.number.isRequired,
      // New statistical fields
      has_transaction_outliers: PropTypes.bool,
      transaction_variability_percent: PropTypes.number,
    })
  ).isRequired,
  formatCurrency: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default Category90DayAverage;
